import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X, Loader2, Wallet, Undo2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import api from "../../console/api";
import { money } from "./FinanceDashboard";

const STATUS = { paid: "bg-[#E4F0E8] text-[#2F6B4F]", partial: "bg-[#FBF3E3] text-[#C49247]", unpaid: "bg-[#FBF0F0] text-[#A33030]" };

export function OrderFinanceDrawer({ orderId, onClose, onChanged }) {
  const [view, setView] = useState(null);
  const [mode, setMode] = useState(null);

  const load = useCallback(async () => {
    try { const r = await api.get(`/admin/finance/order/${orderId}`); setView(r.data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't load that order."); onClose(); }
  }, [orderId, onClose]);
  useEffect(() => { load(); }, [load]);

  const done = async (r) => { setView(r.data); setMode(null); onChanged?.(); };

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/30" onClick={onClose} data-testid="order-finance-drawer">
      <aside className="w-full max-w-md h-full bg-[#FBF6E9] shadow-2xl overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        {!view ? <div className="py-20 text-center"><Loader2 className="w-6 h-6 animate-spin inline text-[#6E1E3A]" /></div> : (
          <div className="p-5 space-y-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-[10px] tracking-[0.3em] uppercase font-semibold text-[#6E1E3A]">{view.kind_label} · {String(view.created_at || "").slice(0, 10)}</div>
                <h2 className="font-serif text-3xl text-[#3C2A1E]">{view.order_number || view.order_id.slice(0, 8)}</h2>
                <div className="text-sm text-[#5A4565]">{view.customer_name} <span className="text-[#9C8B73]">· {view.email}</span></div>
              </div>
              <button type="button" onClick={onClose} className="p-1 text-[#6E1E3A]" data-testid="order-finance-close"><X className="w-5 h-5" /></button>
            </div>

            <div className="grid grid-cols-3 gap-2 text-center">
              <Stat label="Total" value={money(view.total)} color="#3E1F46" testid="order-finance-total" />
              <Stat label="Paid" value={money(view.paid)} color="#2F6B4F" testid="order-finance-paid" />
              <Stat label="Owing" value={money(view.owing)} color={view.owing > 0 ? "#C49247" : "#2F6B4F"} testid="order-finance-owing" />
            </div>
            <div className="flex items-center gap-2 text-xs">
              <span className={`px-2 py-0.5 text-[9px] tracking-[0.2em] uppercase font-semibold ${STATUS[view.status]}`} data-testid="order-finance-status">{view.status === "partial" ? "part paid" : view.status}</span>
              <span className="text-[#7A6A55]">stage: {(view.stage || "").replace("_", " ")}</span>
              {view.refunded > 0 && <span className="text-[#A33030]">refunded {money(view.refunded)}</span>}
              <Link to={`/admin/order/${view.order_id}`} className="ml-auto inline-flex items-center gap-1 text-[#6E1E3A] font-semibold hover:underline" data-testid="order-finance-open-order">Open order <ExternalLink className="w-3 h-3" /></Link>
            </div>

            {!mode && (
              <div className="flex gap-2">
                <button type="button" disabled={view.owing <= 0} onClick={() => setMode("payment")} data-testid="order-finance-record-payment"
                  className="flex-1 h-10 inline-flex items-center justify-center gap-2 text-white text-[11px] tracking-[0.18em] uppercase font-semibold disabled:opacity-40" style={{ backgroundColor: "#2F6B4F" }}><Wallet className="w-4 h-4" /> Record payment</button>
                <button type="button" onClick={() => setMode("refund")} data-testid="order-finance-refund"
                  className="flex-1 h-10 inline-flex items-center justify-center gap-2 border text-[11px] tracking-[0.18em] uppercase font-semibold" style={{ borderColor: "#A33030", color: "#A33030" }}><Undo2 className="w-4 h-4" /> Refund / credit</button>
              </div>
            )}
            {mode === "payment" && <PaymentForm view={view} onCancel={() => setMode(null)} onDone={done} />}
            {mode === "refund" && <RefundForm view={view} onCancel={() => setMode(null)} onDone={done} />}

            <History title="Payments" rows={view.payments} render={(p) => <><span>{String(p.at || "").slice(0, 10)} · {(p.method || "").replace("_", " ")}{p.note ? ` · ${p.note}` : ""}</span><strong className="text-[#2F6B4F]">{money(p.amount)}</strong></>} empty="No payments yet." testid="order-finance-payments" />
            <History title="Refunds & credit notes" rows={view.refunds} render={(r) => <><span>{String(r.issued_at || "").slice(0, 10)} · {r.kind === "credit_note" ? "credit note" : "refund"}{r.full ? " (full)" : ""} · {r.reason}</span><strong className="text-[#A33030]">{money(r.amount)}</strong></>} empty="None." testid="order-finance-refunds" />
          </div>
        )}
      </aside>
    </div>
  );
}

const Stat = ({ label, value, color, testid }) => (
  <div className="bg-white border p-2" style={{ borderColor: "#E8DFCB" }} data-testid={testid}>
    <div className="text-[9px] tracking-[0.25em] uppercase text-[#7A6A55]">{label}</div>
    <div className="font-serif text-lg" style={{ color }}>{value}</div>
  </div>
);

const History = ({ title, rows, render, empty, testid }) => (
  <section data-testid={testid}>
    <h3 className="text-[10px] tracking-[0.3em] uppercase font-semibold text-[#6E1E3A] mb-1">{title}</h3>
    {(rows || []).length === 0 ? <p className="text-xs italic text-[#9C8B73]">{empty}</p> : (
      <ul className="bg-white border divide-y text-xs" style={{ borderColor: "#E8DFCB" }}>
        {rows.map((r, i) => <li key={r.id || i} className="flex justify-between gap-3 px-3 py-2">{render(r)}</li>)}
      </ul>
    )}
  </section>
);

const input = "h-10 px-3 border text-sm bg-white w-full";
const label = "block text-[10px] tracking-[0.2em] uppercase font-semibold text-[#6E1E3A] mb-1";

function PaymentForm({ view, onCancel, onDone }) {
  const [f, setF] = useState({ amount: view.owing.toFixed(2), method: "bank_transfer", date: new Intl.DateTimeFormat("en-CA", { timeZone: "Pacific/Auckland" }).format(new Date()), note: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (Number(f.amount) > view.owing + 0.009) return toast.error(`Only ${money(view.owing)} is still owing on this order.`);
    setBusy(true);
    try {
      const r = await api.post(`/admin/finance/order/${view.order_id}/payment`, { ...f, amount: Number(f.amount) });
      toast.success(r.data.status === "paid" ? "Paid in full , order moves on to Packing." : `Part payment recorded , ${money(r.data.owing)} still owing.`);
      await onDone(r);
    } catch (err) { toast.error(err?.response?.data?.detail || "Couldn't record that payment."); }
    finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="bg-white border p-3 space-y-2" style={{ borderColor: "#2F6B4F" }} data-testid="order-payment-form">
      <div className="grid grid-cols-2 gap-2">
        <label><span className={label}>Amount</span><input type="number" step="0.01" min="0.01" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} className={input} style={{ borderColor: "#E8DFCB" }} data-testid="payment-amount" required /></label>
        <label><span className={label}>Date</span><input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} className={input} style={{ borderColor: "#E8DFCB" }} data-testid="payment-date" /></label>
      </div>
      <label><span className={label}>Method</span>
        <select value={f.method} onChange={(e) => setF({ ...f, method: e.target.value })} className={input} style={{ borderColor: "#E8DFCB" }} data-testid="payment-method">
          <option value="bank_transfer">Bank transfer</option><option value="card">Card</option><option value="cash">Cash</option><option value="heritage_credit">Heritage credit</option><option value="other">Other</option>
        </select></label>
      <label><span className={label}>Note</span><input value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} placeholder="Bank reference, who paid…" className={input} style={{ borderColor: "#E8DFCB" }} data-testid="payment-note" /></label>
      <div className="flex gap-2">
        <button type="submit" disabled={busy} className="flex-1 h-10 text-white text-[11px] tracking-[0.18em] uppercase font-semibold disabled:opacity-50" style={{ backgroundColor: "#2F6B4F" }} data-testid="payment-save">{busy ? "Saving…" : "Save payment"}</button>
        <button type="button" onClick={onCancel} className="h-10 px-3 text-xs text-[#7A6A55]">Cancel</button>
      </div>
    </form>
  );
}

function RefundForm({ view, onCancel, onDone }) {
  const left = Math.max(0, view.total - view.refunded);
  const [f, setF] = useState({ amount: left.toFixed(2), kind: "refund", reason: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (Number(f.amount) > left + 0.009) return toast.error(`Only ${money(left)} is left to refund on this order.`);
    if (left <= 0) return toast.error("This order has already been fully refunded.");
    setBusy(true);
    try {
      const r = await api.post(`/admin/finance/order/${view.order_id}/refund`, { ...f, amount: Number(f.amount) });
      toast.success(f.kind === "credit_note" ? `Credit note issued , ${money(f.amount)} added to the customer's Heritage credits.` : `Refund of ${money(f.amount)} recorded. The original sale stays on the ledger.`);
      await onDone(r);
    } catch (err) { toast.error(err?.response?.data?.detail || "Couldn't record that refund."); }
    finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="bg-white border p-3 space-y-2" style={{ borderColor: "#A33030" }} data-testid="order-refund-form">
      <div className="flex gap-1">
        {[["refund", "Money back"], ["credit_note", "Store credit"]].map(([v, l]) => (
          <button key={v} type="button" onClick={() => setF({ ...f, kind: v })} data-testid={`refund-kind-${v}`}
            className={`flex-1 h-9 text-[10px] tracking-[0.18em] uppercase font-semibold border ${f.kind === v ? "bg-[#A33030] border-[#A33030] text-white" : "border-[#E8DFCB] text-[#5A4565]"}`}>{l}</button>
        ))}
      </div>
      <div className="grid grid-cols-2 gap-2 items-end">
        <label><span className={label}>Amount (max {money(left)})</span><input type="number" step="0.01" min="0.01" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} className={input} style={{ borderColor: "#E8DFCB" }} data-testid="refund-amount" required /></label>
        <button type="button" onClick={() => setF({ ...f, amount: left.toFixed(2) })} className="h-10 border text-xs font-semibold" style={{ borderColor: "#C9A268" }} data-testid="refund-full">Full amount</button>
      </div>
      <label><span className={label}>Reason</span><input value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} placeholder="Why is this being refunded?" className={input} style={{ borderColor: "#E8DFCB" }} data-testid="refund-reason" required /></label>
      <p className="text-[11px] italic text-[#7A6A55]">{f.kind === "credit_note" ? "Adds the amount to the customer's Heritage credits to spend later." : "Records that money went back to the customer. Pay it out via your bank or Stripe separately."}</p>
      <div className="flex gap-2">
        <button type="submit" disabled={busy} className="flex-1 h-10 text-white text-[11px] tracking-[0.18em] uppercase font-semibold disabled:opacity-50" style={{ backgroundColor: "#A33030" }} data-testid="refund-save">{busy ? "Saving…" : f.kind === "credit_note" ? "Issue credit note" : "Record refund"}</button>
        <button type="button" onClick={onCancel} className="h-10 px-3 text-xs text-[#7A6A55]">Cancel</button>
      </div>
    </form>
  );
}
